import { ImageOff, Layers2 } from "lucide-react";
import { Link } from "react-router";

import {
  Badge,
  Button,
  CollapsibleCard,
  cn,
  colorTransitionClassName,
  focusRingClassName,
  insetFocusRingClassName,
  interactiveMotionClassName,
} from "@ceedero/ui";

import { ProjectCoverTransition } from "@/music-catalog/components/project-cover-transition";
import { TRACK_TABLE_ROW_CLASS_NAME, TrackTable } from "@/music-catalog/components/track-table";
import { useTranslation } from "@/localization/hooks/use-translation";
import { ROUTES } from "@/navigation/constants/routes";
import { ProjectMetaBadges } from "@/music-catalog/components/music-catalog-page/project-meta-badges";
import { formatTrackDuration } from "@/music-catalog/utils/catalog-view";
import { resolveProjectArtworkPanels } from "@/music-catalog/utils/project-artwork";
import { resolveProjectDiscWarmUpHandlers } from "@/music-catalog/utils/project-disc-warm-up";

import type { LibraryProject } from "@/cloud/types/types";

/**
 * A project as a row that opens onto its tracklist.
 *
 * The tracks view groups by project rather than listing every track flat,
 * because a track's project is the thing that makes it findable — "Fade Away"
 * appearing three times is only useful once you can see which albums it is on.
 *
 * `skipsOffscreenRendering` is the browser's own virtualization: the rows stay
 * in the DOM, so in-page find, the tab order and a screen reader still reach
 * them, and nothing has to estimate a height it cannot know.
 */
export const ProjectTracksAccordion = ({
  duplicateCount,
  duplicateEntryIds,
  project,
}: {
  duplicateCount: number;
  duplicateEntryIds: Set<string>;
  project: LibraryProject;
}) => {
  const { t } = useTranslation();
  const projectUrl = `${ROUTES.catalog}/${encodeURIComponent(project.desktopProjectId)}`;
  const frontArtwork = resolveProjectArtworkPanels(project).find((panel) => panel.id === "front");
  const discWarmUpHandlers = resolveProjectDiscWarmUpHandlers(project);

  return (
    <CollapsibleCard
      skipsOffscreenRendering
      // Drawn in full while a view transition runs. The browser captures nothing
      // inside skipped content, and a row that has only just mounted is still
      // skipped when the new page is captured, so a cover coming back from its
      // project would have nowhere to land.
      className="[:root:active-view-transition_&]:[content-visibility:visible]"
      title={
        <span className="flex min-w-0 flex-wrap items-center gap-2">
          <Link
            to={projectUrl}
            className={cn(
              "truncate rounded-md hover:underline",
              interactiveMotionClassName,
              focusRingClassName,
            )}
            {...discWarmUpHandlers}
          >
            {project.title}
          </Link>
          <ProjectMetaBadges duplicateCount={duplicateCount} project={project} />
        </span>
      }
      media={
        <Link
          to={projectUrl}
          aria-label={t("catalog.openProjectNamed", { title: project.title })}
          className={cn(
            "shrink-0 cursor-pointer rounded-md",
            interactiveMotionClassName,
            insetFocusRingClassName,
          )}
          {...discWarmUpHandlers}
        >
          {frontArtwork === undefined ? (
            <span className="flex size-20 items-center justify-center rounded-md border border-border bg-muted sm:size-24">
              <ImageOff aria-hidden className="size-8 text-muted-foreground" />
            </span>
          ) : (
            <ProjectCoverTransition desktopProjectId={project.desktopProjectId}>
              <img
                src={frontArtwork.url}
                alt=""
                loading="lazy"
                className="size-20 rounded-md border border-border object-contain sm:size-24"
              />
            </ProjectCoverTransition>
          )}
        </Link>
      }
      actions={
        <Button asChild type="button" variant="outline" size="sm">
          <Link to={projectUrl} {...discWarmUpHandlers}>
            {t("catalog.openProject")}
          </Link>
        </Button>
      }
    >
      {project.tracks.length === 0 ? (
        <p className="p-4 text-sm text-muted-foreground">{t("catalog.projectNoTracks")}</p>
      ) : (
        <TrackTable>
          {project.tracks.map((track) => {
            const duration = formatTrackDuration(track.durationSeconds);
            const isDuplicate = duplicateEntryIds.has(`${project.desktopProjectId}:${track.id}`);
            return (
              <li
                key={track.id}
                className={cn(
                  TRACK_TABLE_ROW_CLASS_NAME,
                  "py-2 text-sm",
                  colorTransitionClassName,
                  "hover:bg-accent/40",
                )}
              >
                <span className="text-center text-muted-foreground tabular-nums">
                  {track.position ?? "—"}
                </span>
                {/*
                  A track's own words, never translated and never tidied: this
                  is the user's data.
                */}
                <span className="flex min-w-0 items-center gap-2">
                  <span className="truncate font-medium">{track.title}</span>
                  {isDuplicate && (
                    <Badge tone="warning">
                      <Layers2 className="size-3" aria-hidden />
                      {t("catalog.duplicate")}
                    </Badge>
                  )}
                </span>
                <span className={cn("truncate", !track.artist && "text-muted-foreground")}>
                  {track.artist || "—"}
                </span>
                {/*
                  Empty rather than "0:00" when the desktop never read a
                  duration: a zero is a claim about the track, and no reading is
                  not one.
                */}
                <span className="text-right text-muted-foreground tabular-nums">
                  {duration ?? ""}
                </span>
              </li>
            );
          })}
        </TrackTable>
      )}
    </CollapsibleCard>
  );
};
